import React, { useEffect, useRef, useState } from 'react'
import { useConfigStore } from '@renderer/stores/configState'
import { useIdleStore } from '@renderer/stores/idleStore'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useTranslation } from 'react-i18next'

export const Screensaver: React.FC = () => {
  const { isScreensaverActive, wakeUp } = useIdleStore()
  const { config } = useConfigStore()
  const { enableScreensaver: localEnableScreensaver } = useSettingsStore()
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [videoError, setVideoError] = useState<string | null>(null)
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)

  const screensaverVideoUrl = config?.theme?.screensaverVideoUrl
  // Use project's enableScreensaver setting if defined, otherwise fall back to local setting
  const enableScreensaver = config?.theme?.enableScreensaver ?? localEnableScreensaver

  useEffect(() => {
    // Reset video error state when screensaver becomes inactive
    if (!isScreensaverActive) {
      setVideoError(null)
      setIsVideoLoaded(false)
    }
  }, [isScreensaverActive])

  useEffect(() => {
    // If the screensaver is active but video has stopped for some reason, ensure playing
    if (isScreensaverActive && videoRef.current && !videoError) {
      videoRef.current.play().catch(console.error)
    }
    // Focus the container so keyboard events work
    if (isScreensaverActive && containerRef.current) {
      containerRef.current.focus()
    }
  }, [isScreensaverActive, videoError])

  useEffect(() => {
    if (!isScreensaverActive) return

    // Attach to document to catch wheel interaction globally
    const handleWheel = (): void => {
      wakeUp()
    }

    document.addEventListener('wheel', handleWheel)

    return (): void => {
      document.removeEventListener('wheel', handleWheel)
    }
  }, [isScreensaverActive, wakeUp])

  const screensaverStateLogRef = useRef<string>('')
  useEffect(() => {
    if (!import.meta.env.DEV) return
    const snapshot = JSON.stringify({
      enableScreensaver,
      projectEnableScreensaver: config?.theme?.enableScreensaver,
      localEnableScreensaver,
      isScreensaverActive,
      screensaverVideoUrl
    })
    if (screensaverStateLogRef.current === snapshot) return
    screensaverStateLogRef.current = snapshot
    console.log('[Screensaver] State:', {
      enableScreensaver,
      projectEnableScreensaver: config?.theme?.enableScreensaver,
      localEnableScreensaver,
      isScreensaverActive,
      screensaverVideoUrl
    })
  }, [
    enableScreensaver,
    config?.theme?.enableScreensaver,
    localEnableScreensaver,
    isScreensaverActive,
    screensaverVideoUrl
  ])

  if (!enableScreensaver || !isScreensaverActive || !screensaverVideoUrl) {
    return null
  }

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>): void => {
    const video = e.currentTarget
    const error = video.error
    const errorMessage = error
      ? `Code ${error.code}: ${error.message || getMediaErrorMessage(error.code)}`
      : 'Unknown error'
    console.error('[Screensaver] Video failed to load:', errorMessage)
    console.error('[Screensaver] Attempted URL:', screensaverVideoUrl)
    setVideoError(errorMessage)
  }

  const getMediaErrorMessage = (code: number): string => {
    switch (code) {
      case 1: return 'MEDIA_ERR_ABORTED - fetching process aborted'
      case 2: return 'MEDIA_ERR_NETWORK - network error'
      case 3: return 'MEDIA_ERR_DECODE - error during decoding'
      case 4: return 'MEDIA_ERR_SRC_NOT_SUPPORTED - format not supported or file not found'
      default: return 'Unknown error'
    }
  }

  const handleVideoLoaded = (): void => {
    console.log('[Screensaver] Video loaded successfully')
    setIsVideoLoaded(true)
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[99999] bg-black cursor-none outline-none"
      onClick={wakeUp}
      onTouchStart={wakeUp}
      onMouseMove={wakeUp}
      onKeyDown={wakeUp}
      tabIndex={0}
      role="dialog"
      aria-label="Screensaver - click or press any key to dismiss"
    >
      {videoError ? (
        <div className="flex items-center justify-center h-full text-white text-center p-8">
          <div>
            <p className="text-xl mb-4">{t('settings.screensaver.tapToContinue')}</p>
            <p className="text-sm text-gray-400 mb-2">Error: {videoError}</p>
            <p className="text-xs text-gray-500 break-all max-w-xl">URL: {screensaverVideoUrl}</p>
          </div>
        </div>
      ) : (
        <video
          ref={videoRef}
          src={screensaverVideoUrl}
          crossOrigin="anonymous"
          className="w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          style={{ pointerEvents: 'none', opacity: isVideoLoaded ? 1 : 0 }}
          onError={handleVideoError}
          onLoadedData={handleVideoLoaded}
        />
      )}
    </div>
  )
}
